// deps
import React, { memo, useCallback, useMemo, useState } from "react";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import {
    generatePaymentMethodId,
    getPaymentGroupDisableMessage,
    getPaymentGroupDisplayName,
    getPaymentMethods,
    isFlipkartCoinsPaymentMethod,
    isPaymentGroupEnabled,
    isPaymentMethodSelected,
    isTwidRewardsPaymentMethod,
} from "@payment-ui/helpers/Payment";

// types
import { RewardsPaymentGroup } from "@payment-ui/interfaces";

// components
import PaymentGroupContainer from "@payment-ui/components/payment-group/components/Container";
import PaymentGroupTitle from "@payment-ui/components/payment-group/components/Title";
import { FlipkartSuperCoinPaymentMethod } from "@payment-ui/components/payment-method/flipkart-super-coins";
import { DisabledSplitpay } from "../components/DisabledSplitpay";

// dls
import { default as ChevronDownIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDown/12";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// constants
import { PAYMENT_GROUP_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { TEXTS } from "@payment-ui/constants";

// styles
import styles from "../PaymentGroup.v4.scss";
import { Icon, IconWrapper, ViewAllRewardsContainer } from "./styles";

function RewardsPG(props: RewardsPaymentGroup): React.ReactElement | null {
    const paymentMethods = getPaymentMethods(props);
    const isGroupEnabled = isPaymentGroupEnabled(props.paymentData);

    const [foldSize, setFoldSize] = useState(props.paymentData.foldSize || Number.MAX_VALUE);

    const viewAllRewardsCta = useMemo(() => foldSize < paymentMethods.length, [foldSize, paymentMethods]);

    const paymentMethodsToShow = useMemo(() => {
        if (paymentMethods.length < foldSize) {
            return paymentMethods;
        }
        return paymentMethods.slice(0, foldSize);
    }, [foldSize, paymentMethods]);

    const handleViewAllClick = useCallback(() => {
        setFoldSize(paymentMethods.length);
    }, [paymentMethods.length]);

    if (isEmpty(paymentMethods)) {
        return null;
    }

    return (
        <PaymentGroupContainer>
            {!props.hideGroupTitle && (
                <PaymentGroupTitle
                    title={getPaymentGroupDisplayName(props.paymentData)}
                    disableMessage={getPaymentGroupDisableMessage(props.paymentData)}
                    data-testid={PAYMENT_GROUP_TEST_IDS.TITLE.REWARDS}
                />
            )}
            <div className={styles.paymentGroupContainer} data-testid={PAYMENT_GROUP_TEST_IDS.REWARDS.CONTENT}>
                {paymentMethodsToShow.map(paymentMethod => {
                    const id = generatePaymentMethodId(paymentMethod);
                    if (isFlipkartCoinsPaymentMethod(paymentMethod)) {
                        return (
                            <FlipkartSuperCoinPaymentMethod
                                id={id}
                                key={paymentMethod.name}
                                methodData={paymentMethod}
                                paymentActions={props.paymentActions}
                                paymentAmount={props.paymentAmount}
                                isPaymentGroupEnabled={isGroupEnabled}
                                selected={isPaymentMethodSelected(props.selectedPaymentId, id)}
                                onFlipkartToolTipShow={props.onFlipkartToolTipShow}
                            />
                        );
                    } else if (isTwidRewardsPaymentMethod(paymentMethod)) {
                        return <DisabledSplitpay key={paymentMethod.name} paymentMethod={paymentMethod} />;
                    }
                })}

                {viewAllRewardsCta && (
                    <ViewAllRewardsContainer
                        onClick={handleViewAllClick}
                        data-testid={PAYMENT_GROUP_TEST_IDS.REWARDS.VIEW_ALL_REWARDS_CTA}
                    >
                        <Text
                            variant="V2.body2.neutral.bold"
                            color="text_Button_Disabled"
                            dangerouslySetInnerHTML={{ __html: TEXTS.VIEW_ALL_REWARDS }}
                        />
                        <IconWrapper>
                            <Icon Icon={ChevronDownIcon} fillColor="text_Button_Disabled" />
                        </IconWrapper>
                    </ViewAllRewardsContainer>
                )}
            </div>
        </PaymentGroupContainer>
    );
}

export default memo(RewardsPG);
